import React, { useState, useEffect } from 'react';
import {
  TextField,
  Grid,
  MenuItem,
  Typography,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import FormDialog from '../Common/FormDialog';
import LocationPicker from '../Map/LocationPicker';
import { ProjectStatus, Project, ProjectFormData } from '../../types';
import api from '../../utils/api';
import ToastNotification from '../../utils/toastNotification';

interface EditProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  project: Project | null;
}

// Lebanon governorates and districts
const LEBANON_GOVERNORATES = [
  'Beirut',
  'Mount Lebanon',
  'North Lebanon',
  'South Lebanon',
  'Nabatieh',
  'Beqaa',
  'Baalbek-Hermel',
  'Akkar',
];

const DISTRICT_MAP: Record<string, string[]> = {
  'Beirut': ['Beirut'],
  'Mount Lebanon': ['Baabda', 'Aley', 'Chouf', 'Keserwan', 'Jbeil', 'Matn'],
  'North Lebanon': ['Tripoli', 'Batroun', 'Bsharri', 'Koura', 'Miniyeh-Danniyeh', 'Zgharta'],
  'South Lebanon': ['Sidon', 'Tyre', 'Jezzine'],
  'Nabatieh': ['Nabatieh', 'Bint Jbeil', 'Hasbaya', 'Marjeyoun'],
  'Beqaa': ['Zahle', 'West Beqaa', 'Rashaya'],
  'Baalbek-Hermel': ['Baalbek', 'Hermel'],
  'Akkar': ['Akkar'],
};

const EditProjectDialog: React.FC<EditProjectDialogProps> = ({
  open,
  onClose,
  onSuccess,
  project,
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    startDate: new Date().toISOString(),
    endDate: '',
    status: ProjectStatus.PLANNING,
    progress: 0,
    ownerName: '',
    ownerPhone: '',
    latitude: 33.8547,
    longitude: 35.8623,
    governorate: '',
    district: '',
    town: '',
    address: '',
    city: '',
    googleMapsLink: '',
    totalFloors: undefined,
    totalApartments: undefined,
    availableApartments: undefined,
    constructionStatus: 'UNDER_CONSTRUCTION',
    completionPercentage: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [districtOptions, setDistrictOptions] = useState<string[]>([]);

  useEffect(() => {
    if (project && open) {
      setFormData({
        name: project.name,
        description: project.description || '',
        startDate: project.startDate,
        endDate: project.endDate || '',
        status: project.status,
        progress: project.progress || 0,
        ownerName: project.ownerName,
        ownerPhone: project.ownerPhone,
        latitude: project.latitude || 33.8547,
        longitude: project.longitude || 35.8623,
        governorate: project.governorate || '',
        district: project.district || '',
        town: project.town || '',
        address: project.address || '',
        city: project.city || '',
        googleMapsLink: project.googleMapsLink || '',
        totalFloors: project.totalFloors,
        totalApartments: project.totalApartments,
        availableApartments: project.availableApartments,
        constructionStatus: project.constructionStatus,
        completionPercentage: project.completionPercentage || 0,
      });

      // Set district options if governorate is set
      if (project.governorate) {
        const districts = DISTRICT_MAP[project.governorate] || [];
        setDistrictOptions(districts);
      }
    }
  }, [project, open]);

  const handleLocationChange = (lat: number, lng: number, googleMapsLink?: string) => {
    setFormData((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      googleMapsLink: googleMapsLink || prev.googleMapsLink,
    }));
  };

  const handleChange = (field: keyof ProjectFormData, value: any) => {
    // If governorate changes, reset district and update district options
    if (field === 'governorate') {
      const districts = value ? (DISTRICT_MAP[value] || []) : [];
      setDistrictOptions(districts);
      setFormData((prev) => ({ ...prev, governorate: value, district: '' }));
      if (errors['governorate']) setErrors((prev) => ({ ...prev, governorate: '' }));
      return;
    }

    // If construction status changes to COMPLETED, set completion to 100%
    if (field === 'constructionStatus' && value === 'COMPLETED') {
      setFormData((prev) => ({ ...prev, constructionStatus: value, completionPercentage: 100 }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
      ToastNotification.error('Please enter a project name');
    }
    if (!formData.ownerName.trim()) {
      newErrors.ownerName = 'Owner name is required';
      ToastNotification.error('Please enter the owner name');
    }
    if (!formData.ownerPhone.trim()) {
      newErrors.ownerPhone = 'Owner phone is required';
      ToastNotification.error('Please enter the owner phone number');
    }

    // Date validation
    if (formData.endDate && new Date(formData.endDate) < new Date(formData.startDate)) {
      newErrors.endDate = 'End date cannot be before start date';
      ToastNotification.error('End date cannot be before start date');
    }

    // Completion percentage validation
    if (formData.constructionStatus === 'UNDER_CONSTRUCTION') {
      if (formData.completionPercentage === undefined || formData.completionPercentage < 0 || formData.completionPercentage > 100) {
        newErrors.completionPercentage = 'Completion percentage must be between 0 and 100';
        ToastNotification.error('Completion percentage must be between 0 and 100');
      }
    }

    // Numeric validations
    if (formData.totalFloors !== undefined && formData.totalFloors <= 0) {
      newErrors.totalFloors = 'Total floors must be greater than 0';
      ToastNotification.error('Total floors must be a positive number');
    }
    if (formData.totalApartments !== undefined && formData.totalApartments < 0) {
      newErrors.totalApartments = 'Total apartments cannot be negative';
      ToastNotification.error('Total apartments cannot be negative');
    }
    if (formData.availableApartments !== undefined && formData.availableApartments < 0) {
      newErrors.availableApartments = 'Available apartments cannot be negative';
      ToastNotification.error('Available apartments cannot be negative');
    }
    if (
      formData.totalApartments !== undefined &&
      formData.availableApartments !== undefined &&
      formData.availableApartments > formData.totalApartments
    ) {
      newErrors.availableApartments = 'Available apartments cannot exceed total apartments';
      ToastNotification.error('Available apartments cannot exceed total apartments');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!project) return;

    if (!validateForm()) {
      ToastNotification.warning('Please fill in all required fields correctly');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || undefined,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
        status: formData.status,
        progress: formData.progress || 0,
        ownerName: formData.ownerName,
        ownerPhone: formData.ownerPhone,
        latitude: formData.latitude,
        longitude: formData.longitude,
        governorate: formData.governorate || undefined,
        district: formData.district || undefined,
        town: formData.town || undefined,
        address: formData.address || undefined,
        city: formData.city || undefined,
        googleMapsLink: formData.googleMapsLink || undefined,
        totalFloors: formData.totalFloors,
        totalApartments: formData.totalApartments,
        availableApartments: formData.availableApartments,
        constructionStatus: formData.constructionStatus,
        completionPercentage: formData.completionPercentage,
      };

      await api.put(`/projects/${project.id}`, payload);
      ToastNotification.success('Project updated successfully!');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error updating project:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update project';
      ToastNotification.error(errorMessage);
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!project) return null;

  return (
    <FormDialog
      open={open}
      title="Edit Project"
      subtitle={`Update details for ${project.name}`}
      onClose={handleClose}
      onSubmit={handleSubmit}
      maxWidth="md"
      submitText="Update Project"
      isSubmitting={isSubmitting}
      disableSubmit={isSubmitting}
    >
      <Grid container spacing={2.5}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
            Basic Information
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Project Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Describe the project details..."
          />
        </Grid>

        {/* Owner Information */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
            Owner Information
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Owner Name"
            value={formData.ownerName}
            onChange={(e) => handleChange('ownerName', e.target.value)}
            error={!!errors.ownerName}
            helperText={errors.ownerName}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Owner Phone"
            value={formData.ownerPhone}
            onChange={(e) => handleChange('ownerPhone', e.target.value)}
            error={!!errors.ownerPhone}
            helperText={errors.ownerPhone}
            required
            placeholder="+961 XX XXX XXX"
          />
        </Grid>

        {/* Location Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
            Location
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={LEBANON_GOVERNORATES}
            value={formData.governorate || null}
            onChange={(_, val) => handleChange('governorate', val || '')}
            freeSolo={false}
            renderInput={(params) => (
              <TextField {...params} label="Governorate" placeholder="Select Governorate" />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={districtOptions}
            value={formData.district || null}
            onChange={(_, val) => handleChange('district', val || '')}
            freeSolo={false}
            disabled={!formData.governorate}
            renderInput={(params) => (
              <TextField
                {...params}
                label="District"
                placeholder={formData.governorate ? 'Select District' : 'Select Governorate first'}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Town/City"
            value={formData.town}
            onChange={(e) => handleChange('town', e.target.value)}
            placeholder="e.g., Hamra, Ashrafieh, Jounieh"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            value={formData.city}
            onChange={(e) => handleChange('city', e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder="Street name, building identifier"
          />
        </Grid>

        {/* Map Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            📍 Location on Map
          </Typography>
          <LocationPicker
            lat={formData.latitude || 33.8547}
            lng={formData.longitude || 35.8623}
            onLocationChange={handleLocationChange}
          />
        </Grid>

        {/* Construction Details */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
            Construction Details
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Construction Status"
            value={formData.constructionStatus}
            onChange={(e) => handleChange('constructionStatus', e.target.value)}
            required
          >
            <MenuItem value="UNDER_CONSTRUCTION">Under Construction</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Completion Percentage"
            type="number"
            value={formData.completionPercentage}
            onChange={(e) => handleChange('completionPercentage', parseFloat(e.target.value))}
            error={!!errors.completionPercentage}
            helperText={errors.completionPercentage}
            disabled={formData.constructionStatus === 'COMPLETED'}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100, step: 1 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Total Floors"
            type="number"
            value={formData.totalFloors || ''}
            onChange={(e) => handleChange('totalFloors', e.target.value ? parseInt(e.target.value) : undefined)}
            error={!!errors.totalFloors}
            helperText={errors.totalFloors}
            placeholder="Number of floors"
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Total Apartments"
            type="number"
            value={formData.totalApartments || ''}
            onChange={(e) => handleChange('totalApartments', e.target.value ? parseInt(e.target.value) : undefined)}
            error={!!errors.totalApartments}
            helperText={errors.totalApartments}
            placeholder="Total number of apartments"
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Available Apartments"
            type="number"
            value={formData.availableApartments || ''}
            onChange={(e) => handleChange('availableApartments', e.target.value ? parseInt(e.target.value) : undefined)}
            error={!!errors.availableApartments}
            helperText={errors.availableApartments}
            placeholder="Currently available"
            inputProps={{ min: 0 }}
          />
        </Grid>

        {/* Project Timeline */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
            Project Timeline
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Project Status"
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value)}
          >
            {Object.values(ProjectStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {status.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Progress"
            type="number"
            value={formData.progress}
            onChange={(e) => handleChange('progress', parseFloat(e.target.value))}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100, step: 1 }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={formData.startDate ? new Date(formData.startDate) : null}
              onChange={(date) => handleChange('startDate', date?.toISOString() || new Date().toISOString())}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="End Date (Expected)"
              value={formData.endDate ? new Date(formData.endDate) : null}
              onChange={(date) => handleChange('endDate', date?.toISOString() || '')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.endDate,
                  helperText: errors.endDate,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    </FormDialog>
  );
};

export default EditProjectDialog;
